import uuidv4 from 'uuid/v4';
import Moment from 'moment-timezone';
import { extendMoment } from 'moment-range';
const moment = extendMoment(Moment);
import Combinatorics from 'js-combinatorics';

const FORMAT_DATE = 'YYYY-MM-DD';
const FORMAT_DATE_TIME_WITH_OFFSET = 'YYYY-MM-DDTHH:mm:ssZ';
const FORMAT_TIME = 'h:mm A';

/*=============================================================================
 Object creation and conversion
 ============================================================================*/

export function createEmptyVueWelcomeMessage() {
  return {
    uuid: uuidv4(),            // String
    guestName: '',             // String
    date: undefined,           // Date which only has YYYY MM DD in local timezone, just for calendar
    startTime: undefined,      // Integer in minutes. 600 means 10 am
    endTime: undefined,        // Integer in minutes. 900 means 3 pm
    errorMessage: undefined,   // not persisted, String for user to notify validation error
  };
}

export function convertContentfulWelcomeMessageForVue(cfStory) {
  const vueStory = createEmptyVueWelcomeMessage();
  vueStory.uuid = cfStory.uuid;
  vueStory.guestName = cfStory.guestName;
  vueStory.date = moment(cfStory.dateString, FORMAT_DATE).toDate(); // moment object in local timezone
  vueStory.startTime = cfStory.startTime;
  vueStory.endTime = cfStory.endTime;
  return vueStory;
}

export function createEmptyContentfulWelcomeMessage() {
  return {
    uuid: '',                  // String
    guestName: '',             // String
    dateString: '',            // String in YYYY-MM-DD format
    startTime: undefined,      // Integer in minutes. 600 means 10 am
    endTime: undefined,        // Integer in minutes. 900 means 3 pm
    startIsoString: undefined, // String, but not loaded back to Vue
    endIsoString: undefined,   // String, but not loaded back to Vue
  };
}

function convertDateStringAndTimeToIsoString(dateString, time, timezoneIdentifier) {
  // Create a moment object in specified timezone whose hour/minute/second components are 0
  const dateTime = moment.tz(dateString, FORMAT_DATE, timezoneIdentifier);

  // Add time in minutes. Now it has all YYYY MM DD HH mm ss Z
  dateTime.add(time, 'm');

  // Convert to ISO format with timezone offset
  return dateTime.format(FORMAT_DATE_TIME_WITH_OFFSET);
}

export function convertVueWelcomeMessageForContentful(vueStory, timezoneIdentifier) {
  const cfStory = createEmptyContentfulWelcomeMessage();
  cfStory.uuid = vueStory.uuid;
  cfStory.guestName = vueStory.guestName;
  cfStory.dateString = moment(vueStory.date).format(FORMAT_DATE);
  cfStory.startTime = vueStory.startTime;
  cfStory.endTime = vueStory.endTime;
  cfStory.startIsoString = convertDateStringAndTimeToIsoString(cfStory.dateString, cfStory.startTime, timezoneIdentifier);
  cfStory.endIsoString = convertDateStringAndTimeToIsoString(cfStory.dateString, cfStory.endTime, timezoneIdentifier);
  return cfStory;
}

/*=============================================================================
 Test for overlap between any welcome message time ranges
 ============================================================================*/
export function validateAllTimeRanges(messages){
  // nothing to test against
  if( messages.length < 2 ){
    return false;
  }

  const pairs = Combinatorics.combination(messages, 2);
  let hasErrors = false;

  pairs.forEach((pair) => {
    const msg1 = pair[0];
    const msg2 = pair[1];

    // skip comparison between pair with empty date(s)
    if( msg1.endTime === undefined || msg1.startTime === undefined || 
      msg2.endTime === undefined || msg2.endTime === undefined )
    {
      return;
    }

    const msg1_start = moment(msg1.date, FORMAT_DATE).add(msg1.startTime, 'minutes');
    const msg1_end = moment(msg1.date, FORMAT_DATE).add(msg1.endTime, 'minutes');
    
    const msg2_start = moment(msg2.date, FORMAT_DATE).add(msg2.startTime, 'minutes');
    const msg2_end = moment(msg2.date, FORMAT_DATE).add(msg2.endTime, 'minutes');

    const range1 = moment.range(msg1_start, msg1_end);
    const range2 = moment.range(msg2_start, msg2_end);

    const isOverlapping = range1.overlaps(range2);

    if( isOverlapping ){
      // add error to later message
      msg2.errorMessage = 'Specified times overlap with another welcome message.'
      hasErrors = true;
    }
  });

  return hasErrors;
}

/*=============================================================================
 Object validation
 ============================================================================*/

export function validateVueWelcomeMessage(message) {
  if (!message.uuid || message.uuid.length === 0) {
    console.error('Missing UUID. This should not happen.');
    return 'UUID is required';
  }

  if (!message.guestName || message.guestName.length === 0) {
    return 'Guest name is required';
  }

  if (!message.date) {
    return 'Date is required';
  }

  if (!message.startTime) {
    return 'Start time is required';
  }

  if (!message.endTime) {
    return 'End time is required';
  }

  if (message.startTime >= message.endTime) {
    return 'Start time must be earlier than end time';
  }

  return null;
}

/*=============================================================================
 StartTime and EndTime pulldown
 ============================================================================*/

// 5am is minimum for pulldown
const MIN_HOUR = 5, MIN_MINUTES = 0;

// 10pm is maximum for pulldown, inclusive
const MAX_HOUR = 22, MAX_MINUTES = 0;

// Show options for every 10 minutes
const INTERVAL_MINUTES = 10;

export function fullOptionsForTime() {
  const options = [];
  const midnightOfDay = moment().startOf('day');
  const maxPointInDay = moment().startOf('day').hours(MAX_HOUR).minutes(MAX_MINUTES);

  // Iterate from minimum point in day to max with specified interval
  for (
    let pointInDay = moment().startOf('day').hours(MIN_HOUR).minutes(MIN_MINUTES);
    pointInDay.isSameOrBefore(maxPointInDay);
    pointInDay.add(INTERVAL_MINUTES, 'm')
  ) {
    const duration = moment.duration(pointInDay.diff(midnightOfDay));
    options.push({value: duration.asMinutes(), label: pointInDay.format(FORMAT_TIME)});
  }

  return options;
}

/*=============================================================================
 Timezone
 ============================================================================*/

export const FULL_OPTIONS_FOR_TIMEZONE = [
  { displayName: 'Eastern', identifier: 'America/New_York', abbr: ['EST', 'EDT'] },
  { displayName: 'Central', identifier: 'America/Chicago', abbr: ['CST', 'CDT'] },
  { displayName: 'Mountain', identifier: 'America/Denver', abbr: ['MST', 'MDT'] },
  { displayName: 'Mountain w/o daylight saving', identifier: 'America/Phoenix', abbr: ['MST'] },
  { displayName: 'Pacific', identifier: 'America/Los_Angeles', abbr: ['PST', 'PDT'] },
];

export function getTimezoneAbbrWithTimezoneAndDate(timezone, d) {
  // d is a JS Date object specified in local timezone.
  // It only has year/month/date components, and hour/min/seconds are 0
  // because of the specification of element ui date picker
  if (d) {
    // Add 5 hours (1000 milliseconds * 60 seconds * 60 minutes * 5 hours)
    // to avoid daylight saving issue. For example, 1:30 am will be problematic
    // on days when daylight saving start/end
    const timestamp = d.getTime() + 1000 * 60 * 60 * MIN_HOUR;
    // The name of timezone is calculate at the point of 5 am of given date
    return timezone.abbr(timestamp);
  } else {
    return '';
  }
}

export function getDefaultTimezoneIdentifier() {
  // Search based on identifier
  const timezoneIdentifier = moment.tz.guess();
  // If identifier matches, use it
  let candidate = FULL_OPTIONS_FOR_TIMEZONE.filter(t => t.identifier === timezoneIdentifier);
  if (candidate.length > 0) {
    return candidate[0].identifier;
  }

  // Search based on abbr
  const timezone = moment.tz.zone(timezoneIdentifier);
  const now = new Date();
  const abbr = modelUtil.getTimezoneAbbrWithTimezoneAndDate(timezone, now);
  // If abbr matches, use it. Mountain time could be ambiguous, but it's fine for now.
  candidate = FULL_OPTIONS_FOR_TIMEZONE.filter(t => t.abbr.indexOf(abbr) !== -1);
  if (candidate.length > 0) {
    return candidate[0].identifier;
  }

  console.error('Default timezone not found');
  return '';
}
